import { useState, useEffect, useCallback, useRef } from "react";

import {
  syncOfflineData,
  getOutboxStatus,
  retrySync,
  purgeFailedItems,
} from "../lib/api";
import { crashLogger } from "../lib/crash-logger";

export function useOfflineStatus() {

  const [isOnline, setIsOnline] =
    useState(navigator.onLine);

  const [outboxStatus, setOutboxStatus] =
    useState({
      total: 0,
      failed: 0,
      pending: 0,
    });

  const [isSyncing, setIsSyncing] =
    useState(false);

  const [sessionExpired, setSessionExpired] =
    useState(false);

  const [sessionExpiredCount, setSessionExpiredCount] =
    useState(0);

  // Metrics: track sync duration between sync-start and sync-complete events
  const syncTimerRef = useRef(null);

  /* ============================= */
  /* REFRESH */
  /* ============================= */

  const refreshStatus =
    useCallback(async () => {

      try {

        const status =
          await getOutboxStatus();

        setOutboxStatus({
          total:
            status?.total || 0,

          failed:
            status?.failed || 0,

          pending:
            status?.pending || 0,
        });

      } catch (err) {

        console.error(
          "[useOfflineStatus] refresh error:",
          err
        );

      }

    }, []);

  /* ============================= */
  /* EVENTS */
  /* ============================= */

  useEffect(() => {

    let mounted = true;

    const safeRefresh =
      async () => {

        if (!mounted) return;

        await refreshStatus();
      };

    const handleOnline =
      async () => {

        if (!mounted) return;

        setIsOnline(true);

        try {
          await syncOfflineData();
        } catch (err) {
          console.error(
            "[useOfflineStatus] sync error:",
            err
          );
        }
      };

    const handleOffline = () => {

      if (!mounted) return;

      setIsOnline(false);
    };

    const handleOutboxChange =
      () => {
        safeRefresh();
      };

    const handleSyncStart =
      (event) => {

        if (!mounted) return;

        setIsSyncing(true);
        // A sync starting means we have an active session — clear any stale expired-session flag
        // so SyncStatus stops showing "Inicia sesión para sincronizar" after token refresh / re-login.
        setSessionExpired(false);
        setSessionExpiredCount(0);
        syncTimerRef.current = crashLogger.startTimer('sync_duration', {
          items: event?.detail?.count ?? 0,
        });
      };

    const handleSyncComplete =
      async () => {

        if (!mounted) return;

        setIsSyncing(false);
        syncTimerRef.current?.();
        syncTimerRef.current = null;

        await safeRefresh();

        // Emit queue size after sync so we can track drain rate
        try {
          const s = await getOutboxStatus();
          crashLogger.metric('queue_size', s?.total ?? 0);
          if ((s?.failed ?? 0) > 0) {
            crashLogger.metric('queue_failed', s.failed);
          }
        } catch { /* best-effort */ }
      };

    const handleSyncError =
      async () => {

        if (!mounted) return;

        setIsSyncing(false);
        syncTimerRef.current?.();
        syncTimerRef.current = null;

        await safeRefresh();
      };

    const handleSessionExpired =
      (event) => {
        if (!mounted) return;
        setSessionExpired(true);
        setSessionExpiredCount(event?.detail?.count ?? 0);
      };

    /* ============================= */
    /* LISTENERS */
    /* ============================= */

    window.addEventListener(
      "online",
      handleOnline
    );

    window.addEventListener(
      "offline",
      handleOffline
    );

    window.addEventListener(
      "outbox-changed",
      handleOutboxChange
    );

    window.addEventListener(
      "sync-start",
      handleSyncStart
    );

    window.addEventListener(
      "sync-complete",
      handleSyncComplete
    );

    // 🔥 IMPORTANTE
    // Evita spinner infinito
    window.addEventListener(
      "sync-error",
      handleSyncError
    );

    window.addEventListener(
      "sync-session-expired",
      handleSessionExpired
    );

    /* ============================= */
    /* CROSS-TAB OUTBOX CHANNEL */
    /* ============================= */

    // Receives 'outbox-changed' messages from sibling tabs (notifyOutboxChange in api.js).
    // Without this, Tab A's pendingCount stays stale after Tab B syncs the shared IndexedDB outbox.
    let outboxChannel = null;
    try {
      outboxChannel = new BroadcastChannel('keves_outbox');
      outboxChannel.onmessage = ({ data }) => {
        if (data?.type === 'outbox-changed' && mounted) {
          safeRefresh();
        }
      };
    } catch { /* BroadcastChannel unavailable — degrade silently */ }

    /* ============================= */
    /* INITIAL LOAD */
    /* ============================= */

    safeRefresh();

    /* ============================= */
    /* CLEANUP */
    /* ============================= */

    return () => {

      mounted = false;

      outboxChannel?.close();

      window.removeEventListener(
        "online",
        handleOnline
      );

      window.removeEventListener(
        "offline",
        handleOffline
      );

      window.removeEventListener(
        "outbox-changed",
        handleOutboxChange
      );

      window.removeEventListener(
        "sync-start",
        handleSyncStart
      );

      window.removeEventListener(
        "sync-complete",
        handleSyncComplete
      );

      window.removeEventListener(
        "sync-error",
        handleSyncError
      );

      window.removeEventListener(
        "sync-session-expired",
        handleSessionExpired
      );
    };

  }, [refreshStatus]);

  /* ============================= */
  /* ACTIONS */
  /* ============================= */

  const forceSync =
    useCallback(async () => {

      if (
        isSyncing ||
        !navigator.onLine
      ) {
        return;
      }

      try {

        setIsSyncing(true);

        await syncOfflineData();

      } catch (err) {

        console.error(
          "[useOfflineStatus] forceSync error:",
          err
        );

      } finally {

        setIsSyncing(false);

        refreshStatus();
      }

    }, [isSyncing, refreshStatus]);

  const retryFailed =
    useCallback(async () => {

      if (isSyncing) {
        return;
      }

      try {

        setIsSyncing(true);

        await retrySync();

      } catch (err) {

        console.error(
          "[useOfflineStatus] retry error:",
          err
        );

      } finally {

        setIsSyncing(false);

        refreshStatus();
      }

    }, [isSyncing, refreshStatus]);

  const purgeFailed =
    useCallback(async () => {

      if (isSyncing) return;

      try {
        await purgeFailedItems();
      } catch (err) {
        console.error(
          "[useOfflineStatus] purge error:",
          err
        );
      } finally {
        refreshStatus();
      }

    }, [isSyncing, refreshStatus]);

  /* ============================= */
  /* RETURN */
  /* ============================= */

  return {
    isOnline,

    outboxCount:
      outboxStatus.total || 0,

    outboxStatus,

    isSyncing,

    sessionExpired,
    sessionExpiredCount,

    forceSync,

    retryFailed,

    purgeFailed,
  };
}

/* ========================================= */
/* BACKWARD COMPAT */
/* ========================================= */

export function useOnlineSync() {

  const result =
    useOfflineStatus();

  return {
    ...result,

    pendingCount:
      result.outboxStatus.total || 0,
  };
}

// Re-export purgeFailedItems for direct use in Settings / admin UI
export { purgeFailedItems } from '../lib/api';